import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import GuidService from './guid-service';
import SubscribableService from './subscribable-service';

const notificationMessages = defineMessages({
  successTitle: {
    description: 'Title for a success message',
    defaultMessage: 'Success',
    id: '1cSjsn',
  },
  errorTitle: {
    description: 'Title for an error message',
    defaultMessage: 'Error',
    id: '0kLO2P',
  },
  errorContent: {
    description: 'Content for an error message',
    defaultMessage: 'Please try again.',
    id: 'JOOJke',
  },
});

const notifications = {};
const notificationsToRemove = {};

/*
Some guidelines for choosing an appropriate duration:
1) user-servicable errors (i.e. errors where the user has to fix something) => NO DURATION
2) we know exactly what happen errors and the seller can't do anything => LONG
3) we have no idea what happened => SHORT OR MEDIUM
*/
export const NOTIFICATION_DURATIONS = {
  SHORT: 3000,
  MEDIUM: 10000,
  LONG: 30000,
};

const DEFAULT_NOTIFICATION_DURATION = 30000;
const NOTIFICATION_HIDE_ANIMATION_DURATION = 600;

export const NOTIFICATION_TYPE_SUCCESS = 'success';
export const NOTIFICATION_TYPE_WARNING = 'warning';
export const NOTIFICATION_TYPE_INFO = 'info';
export const NOTIFICATION_TYPE_ERROR = 'error';
export const NOTIFICATION_TYPES = [NOTIFICATION_TYPE_SUCCESS, NOTIFICATION_TYPE_WARNING, NOTIFICATION_TYPE_INFO, NOTIFICATION_TYPE_ERROR];

/**
 * @typedef NotificationConfig
 * @type {object}
 * @property {string|JSX.Element} [title]
 * @property {string|JSX.Element} [content]
 * @property {string} [notificationId]
 * @property {string} [notificationType]
 * @property {boolean} [dontHide]
 * @property {number} [duration=30000ms]
 * @property {number} [progressPercentage]
 */

class NotificationService extends SubscribableService {
  createNotificationId() {
    return GuidService.newGuid();
  }

  getNotificationElementId(notificationId) {
    return `new-notification-${notificationId}`;
  }

  /**
   * display a notification component based on the provided notificationConfig
   * @param {NotificationConfig} notificationConfig
   * @returns {notificationId}
   */
  showNotification(notificationConfig) {
    const notificationId = notificationConfig.notificationId || this.createNotificationId();
    // this code relies on the notificationConfig being the same object reference
    // eslint-disable-next-line
    notificationConfig.id = notificationId;
    // eslint-disable-next-line
    notificationConfig.elementId = this.getNotificationElementId(notificationId);
    notifications[notificationId] = notificationConfig;
    this.onChange();
    if (!notificationConfig.dontHide && !notificationConfig.duration) {
      // eslint-disable-next-line
      notificationConfig.duration = DEFAULT_NOTIFICATION_DURATION;
    }
    if (!notificationConfig.dontHide && notificationConfig.progressPercentage === undefined) {
      setTimeout(() => this.removeNotification(notificationId), notificationConfig.duration);
    }
    return notificationId;
  }

  updateNotification(notificationId, notificationConfig) {
    const existingNotification = notifications[notificationId];
    if (existingNotification === undefined) {
      return;
    }
    notifications[notificationId] = {
      ...existingNotification,
      ...notificationConfig,
    };
    const newNotification = notifications[notificationId];
    if (!newNotification.duration && !newNotification.dontHide) {
      newNotification.duration = DEFAULT_NOTIFICATION_DURATION;
    }

    if (newNotification.progressPercentage !== undefined && newNotification.progressPercentage >= 100) {
      setTimeout(() => this.removeNotification(notificationId), newNotification.duration);
    }
    if (!newNotification.dontHide && existingNotification.dontHide) {
      setTimeout(() => this.removeNotification(notificationId), newNotification.duration);
    }
    this.onChange();
  }

  removeNotification = (notificationId) => {
    notificationsToRemove[notificationId] = notifications[notificationId];
    this.onChange();
    setTimeout(() => {
      this.completelyRemoveNotification(notificationId);
    }, NOTIFICATION_HIDE_ANIMATION_DURATION);
  };

  completelyRemoveNotification(notificationId) {
    const notificationConfig = notifications[notificationId];
    if (notificationConfig && notificationConfig.onClose) {
      notificationConfig.onClose();
    }
    delete notifications[notificationId];
    delete notificationsToRemove[notificationId];
    this.onChange();
  }

  getNotifications() {
    return notifications;
  }

  getNotificationsToRemove() {
    return notificationsToRemove;
  }

  getProgressBarThemeFromNotificationType(notificationType) {
    switch (notificationType) {
      case NOTIFICATION_TYPE_SUCCESS:
        return 'create';
      case NOTIFICATION_TYPE_ERROR:
        return 'danger';
      case NOTIFICATION_TYPE_WARNING:
        return 'warning';
      default:
        return 'primary';
    }
  }

  clearNotifications() {
    Object.keys(notifications).forEach(this.removeNotification);
  }

  /**
   * Shows a generic success notification with optional custom title and content
   * @param {string|React.ReactNode} [title] - The title of the notification
   * @param {string|React.ReactNode} [content] - The content of the notification
   * @returns {string} The notification ID
   */
  showGenericSuccessNotification = (title = <FormattedMessage {...notificationMessages.successTitle} />, content) => {
    return this.showNotification({
      notificationType: NOTIFICATION_TYPE_SUCCESS,
      title,
      content,
      duration: NOTIFICATION_DURATIONS.SHORT,
    });
  };

  /**
   * Shows a generic failure notification with optional custom title and content
   * @param {string|React.ReactNode} [title] - The title of the notification
   * @param {string|React.ReactNode} [content] - The content of the notification
   * @returns {string} The notification ID
   */
  showGenericFailureNotification = (
    title = <FormattedMessage {...notificationMessages.errorTitle} />,
    content = <FormattedMessage {...notificationMessages.errorContent} />,
  ) => {
    return this.showNotification({
      notificationType: NOTIFICATION_TYPE_ERROR,
      title,
      content,
      duration: NOTIFICATION_DURATIONS.SHORT,
    });
  };
}

export default new NotificationService();
